import { Trans, useTranslation } from 'react-i18next'
import clsx from 'clsx'

import * as Icon from '@makyoapp/makyo-icon/icon'
import { Button, ButtonVariant, SimpleInput } from '@makyoapp/makyo-ui'

import { APP_VERSION } from '@/configs/version'
import { Info } from '@/components/Info'

// import { ReactComponent as Logo } from '@/assets/logo-mark.svg?react'
import MakyoLogo from '@/assets/makyo-logo-full-color.png'

import Check2FaNotice from './component/Check2FaNotice'
import CheckEmailNotice from './component/CheckEmailNotice'
import InviteHeader from './component/InviteHeader'
import NormalHeader from './component/NormalHeader'
import Notice from './component/Notice'
import RestoreAccountModal from './component/RestoreAccountModal'
import Terms from './component/Terms'
// import ThirdPartyButton from './component/ThirdPartyButton'
import useLogin from './hooks/useLogin'

/**
 * the OR text
 */
function Or() {
  const { t } = useTranslation()
  return (
    <div className='flex flex-row w-full my-2'>
      <div className='py-2 separator w-full text-zinc-500'>{t('OR')}</div>
    </div>
  )
}

/**
 * Login is a function component for rendering the login page.
 * It contains state variables and methods for handling user interactions such as input changes and button clicks.
 * It uses the useTranslation hook from react-i18next for localization.
 *
 * @returns {JSX.Element} A div element representing the login page.
 */
function Login() {
  const { t } = useTranslation()
  const {
    // State variables
    email,
    code,
    twoFaCode,
    buttonDisabled,
    state,
    timeLeft,
    showRestore,
    isRestoreLoading,
    isLoginPending,
    isResend,
    isInvite,
    data,

    // Methods
    handleKeyUp,
    onSubmit,
    handleEmailInput,
    handleCodeInput,
    handleResendCode,
    handleInputTFA,
    handleDeleteAccount,
    handleLogout,
    handleRestoreAccount,
  } = useLogin()

  return (
    <div
      id='login-page'
      //className='flex flex-1 min-h-[100vh] w-full sm:py-8 items-center justify-center sm:bg-zinc-200'
      className='flex justify-center items-center h-full min-h-full overflow-y-auto sm:py-8 sm:bg-zinc-200'
    >
      <div
        //className='bg-white rounded-xl w-[360px] text-zinc-700 p-8 flex flex-col items-center'
        className='bg-white p-8 h-fit rounded-xl w-[360px] text-zinc-700 relative'
      >
        <div className='absolute text-m-sm right-2 top-1'>v{APP_VERSION}</div>

        <div className='mb-2 flex justify-center'>
          {/* <Logo height='64px' width='64px' className='fill-teal-600' /> */}
          <img src={MakyoLogo} className='w-auto h-[42px]' />
        </div>

        {!isInvite ? <NormalHeader step={state.step} /> : <InviteHeader data={data} step={state.step} />}

        <div className='pt-4 w-full'>
          {state.step === 1 && (
            <>
              <Info className='px-2 py-2 mb-4 text-m-sm bg-blue-100 rounded-sm'>
                {t("An account will be created if you don't have one")}
              </Info>

              {/* <ThirdPartyButton /> */}
              {/* <Or /> */}

              <div className='field mb-2'>
                <SimpleInput
                  id='email'
                  outlined={true}
                  name='email'
                  type='email'
                  autoComplete='email'
                  error={state.errorMsg?.toString()}
                  className={clsx('w-full', state.errorMsg && 'border-red-600')}
                  value={email}
                  onChange={handleEmailInput}
                  placeholder={t('Please enter email')}
                  //onKeyUp={handleKeyUp}
                />
              </div>
            </>
          )}

          {state.step === 2 && (
            <>
              <CheckEmailNotice email={email} />

              <div className='field mb-2'>
                <SimpleInput
                  outlined={true}
                  id='code'
                  name='code'
                  type='text'
                  error={state.errorMsg?.toString()}
                  value={code}
                  onChange={handleCodeInput}
                  placeholder={t('Code')}
                  onKeyUp={handleKeyUp}
                />
              </div>
              <div className='field mb-4'>
                <span>{t("Don't receive otp?")}</span>{' '}
                <button
                  className={clsx(timeLeft <= 0 && 'text-primary-600')}
                  disabled={timeLeft > 0}
                  onClick={handleResendCode}
                >
                  {timeLeft > 0 ? (
                    <>
                      {t('Resend')} {timeLeft / 1000}
                    </>
                  ) : (
                    t('Resend')
                  )}
                </button>
              </div>
            </>
          )}

          {state.step === 3 && (
            <>
              <Check2FaNotice />

              <div className='field mb-2'>
                <SimpleInput
                  outlined={true}
                  id='two-fa-code'
                  name='two-fa-code'
                  type='text'
                  error={state.errorMsg?.toString()}
                  value={twoFaCode}
                  onChange={handleInputTFA}
                  placeholder={t('Code')}
                  onKeyUp={handleKeyUp}
                />
              </div>
            </>
          )}

          <Button
            id='login-submit'
            type='button'
            onClick={onSubmit}
            variant={ButtonVariant.Normal}
            disabled={buttonDisabled}
            className='w-full flex flex-row justify-center items-center gap-2'
          >
            {isLoginPending ? <span className='loading spin' /> : <Icon.Login16 />}
            {state.step === 1 ? t('Continue with Email') : isResend ? t('Resending code') : t('Continue')}
          </Button>

          {state.step === 1 && (
            <>
              <Notice className='mt-4' icon={<Icon.PaperPlane24 className='fill-zinc-500' />}>
                <Trans i18nKey='signInNote'>
                  We’ll email you a one-time <strong>PIN</strong> for a password-free experience
                </Trans>
              </Notice>
            </>
          )}

          <Terms className='text-m-sm mt-2' />
        </div>
      </div>
      {Boolean(showRestore) && (
        <RestoreAccountModal
          isLoading={isRestoreLoading}
          onDelete={handleDeleteAccount}
          onHide={handleLogout}
          onRestore={handleRestoreAccount}
        />
      )}
    </div>
  )
}

export default Login
